import { supabase } from '@/lib/supabase';
import { logger } from '@/lib/utils/logger';
import { config } from '@/lib/config';

const API_BASE_URL = config.apiURL || 'http://localhost:8000';

export interface SATResponse {
    token: string;
    expires_in: number;
}

const getAuthToken = async (): Promise<string> => {
    const { data: { session }, error } = await supabase.auth.getSession();

    if (error) {
        logger.error('[SAT Service] Failed to retrieve Supabase session', error);
        throw new Error('Failed to retrieve user session');
    }

    if (!session?.access_token) {
        throw new Error('Not authenticated');
    }

    return session.access_token;
};

const parseErrorResponse = async (response: Response): Promise<string> => {
    try {
        const errorBody = await response.json();
        if (typeof errorBody?.detail === 'string') return errorBody.detail;
        if (typeof errorBody?.message === 'string') return errorBody.message;
    } catch {
        // Ignore JSON parse errors and fall back to status text.
    }

    return response.statusText || `HTTP ${response.status}`;
};

export const generateSAT = async (agentId: string): Promise<SATResponse> => {
    if (!agentId?.trim()) {
        throw new Error('agentId is required');
    }

    try {
        const token = await getAuthToken();

        const response = await fetch(`${API_BASE_URL}/api/v1/sat/generate`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ agent_id: agentId }),
        });

        if (!response.ok) {
            const errorMessage = await parseErrorResponse(response);
            logger.error('[SAT Service] SAT generation failed', {
                agentId,
                status: response.status,
                errorMessage,
            });
            throw new Error(`Failed to generate SAT: ${errorMessage}`);
        }

        const data: unknown = await response.json();
        const satData = data as Partial<SATResponse>;

        if (typeof satData.token !== 'string' || typeof satData.expires_in !== 'number') {
            logger.error('[SAT Service] Invalid SAT response shape', { agentId, data });
            throw new Error('Invalid SAT response from server');
        }

        return {
            token: satData.token,
            expires_in: satData.expires_in,
        };
    } catch (error) {
        logger.error('[SAT Service] Error generating SAT', { agentId, error });
        throw error instanceof Error ? error : new Error('Unexpected error generating SAT');
    }
};
